import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../../lib/api'
import type { AgentRecord } from '../../../shared/types/agents'
import type { ContextMessage } from '../../../shared/types/context'
import { TextContentViewer } from '../TextContentViewer'
import { TASK_DRAG_TYPE } from '../../../shared/lib/dragTypes'
import { bus, firehose } from '../../App'
import { ToolCallBadge } from '../ToolCallBadge'
import { parseMessageBlocks } from '../../lib/messageBlocks'
import { models } from '../../../shared/lib/models'

type AgentTaskAssignment = {
  id: string
  content: string
  sourceHash: string | null
  createdAt: number
}

type AssignmentMap = Record<string, AgentTaskAssignment[]>

type ToolActivityEntry = {
  id: string
  toolName?: string
  content: string
  resultContent?: string
  timestamp: number
}

type AgentActivityState = {
  badges: ToolActivityEntry[]
  deltaText: string
}

type FirehoseEventPayload = {
  agentId: string | null
  payload: any
}

const ASSIGNMENT_STORAGE_KEY = 'agent-task-assignments'
const MAX_ACTIVITY_BADGES = 6
const MAX_DELTA_LENGTH = 2000
const ASSIGNMENT_DRAG_TYPE = 'application/assignment-task'

const normalizeAgent = (row: any): AgentRecord => ({
  id: String(row?.id ?? ''),
  name: row?.name ?? null,
  system_prompt_hash: row?.system_prompt_hash ?? null,
  provider: row?.provider ?? null,
  model: row?.model ?? null,
  tools: row?.tools ?? null,
  last_message_id: row?.last_message_id ?? null,
  state: row?.state ?? null,
  created_at: Number(row?.created_at ?? Date.now()),
  updated_at: Number(row?.updated_at ?? Date.now()),
  last_run_at: row?.last_run_at ?? null,
  tag_entity_id: row?.tag_entity_id ?? null,
})

const generateAssignmentId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `assignment-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

const formatAssignmentTimestamp = (value: number): string => {
  try {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) {
      return ''
    }
    return date.toLocaleString([], { hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric' })
  } catch {
    return ''
  }
}

const createEmptyActivityState = (): AgentActivityState => ({ badges: [], deltaText: '' })

const stringifyToolArguments = (value: unknown): string => {
  if (typeof value === 'string') {
    return value
  }
  try {
    return JSON.stringify(value ?? {}, null, 2)
  } catch {
    return String(value ?? '')
  }
}

const extractToolActivityFromMessages = (messages: ContextMessage[]): ToolActivityEntry[] => {
  const entries: ToolActivityEntry[] = []
  const entryById = new Map<string, ToolActivityEntry>()

  for (const message of messages) {
    const blocks = parseMessageBlocks(message.content)
    for (const block of blocks) {
      if (block.type === 'tool_call') {
        const id = block.toolId ?? generateAssignmentId()
        const entry: ToolActivityEntry = {
          id,
          toolName: block.toolName,
          content: block.content,
          timestamp: message.timestamp,
        }
        entries.push(entry)
        entryById.set(id, entry)
      } else if (block.type === 'tool_result' && block.toolId) {
        const existing = entryById.get(block.toolId)
        if (existing) {
          existing.resultContent = block.content
        }
      }
    }
  }

  return entries
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, MAX_ACTIVITY_BADGES)
    .map((entry) => ({ ...entry }))
}

const normalizeDeltaPayload = (value: unknown): string => {
  if (typeof value === 'string') {
    return value
  }
  if (value === null || value === undefined) {
    return ''
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }
  return String(value)
}

export function AssignmentsPanel() {
  const [agents, setAgents] = useState<AgentRecord[]>([])
  const [loadingAgents, setLoadingAgents] = useState(true)
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null)
  const [activeAgentId, setActiveAgentId] = useState<string | null>(null)
  const [assignments, setAssignments] = useState<AssignmentMap>({})
  const [panelError, setPanelError] = useState<string | null>(null)
  const [statusData, setStatusData] = useState('Select an agent to view status information')
  const [statusRefreshing, setStatusRefreshing] = useState(false)
  const [dropTargetAgentId, setDropTargetAgentId] = useState<string | null>(null)
  const [agentActivity, setAgentActivity] = useState<Record<string, AgentActivityState>>({})
  const [agentStatusMap, setAgentStatusMap] = useState<Record<string, 'idle' | 'running' | 'error'>>({})
  const [taskDropIndex, setTaskDropIndex] = useState<number | null>(null)
  const mountedRef = useRef(true)
  const selectedAgentIdRef = useRef<string | null>(null)
  const activeAgentIdRef = useRef<string | null>(null)
  const lastStatusAgentRef = useRef<string | null>(null)
  const subscribedAgentsRef = useRef<Set<string>>(new Set())
  const pendingToolCallAgentRef = useRef<Map<string, string>>(new Map())
  const taskListRef = useRef<HTMLUListElement | null>(null)
  const draggingTaskRef = useRef<{ agentId: string; taskId: string } | null>(null)
  const taskDropHandledRef = useRef(false)
  const modelLabelMap = useMemo(() => {
    const map: Record<string, string> = {}
    for (const entry of models) {
      map[entry.model] = entry.label
      map[entry.key] = entry.label
    }
    return map
  }, [])

  const updateAgentActivity = useCallback((agentId: string, updater: (state: AgentActivityState) => AgentActivityState) => {
    if (!agentId) {
      return
    }
    setAgentActivity((prev) => {
      const current = prev[agentId] ?? createEmptyActivityState()
      const next = updater(current)
      if (next === current) {
        return prev
      }
      return {
        ...prev,
        [agentId]: next,
      }
    })
  }, [])

  const appendAgentDelta = useCallback(
    (agentId: string, chunk: string) => {
      if (!chunk) {
        return
      }
      updateAgentActivity(agentId, (current) => {
        const nextText = (current.deltaText + chunk).slice(-MAX_DELTA_LENGTH)
        if (nextText === current.deltaText) {
          return current
        }
        return {
          ...current,
          deltaText: nextText,
        }
      })
    },
    [updateAgentActivity],
  )

  const resetAgentDelta = useCallback(
    (agentId: string) => {
      updateAgentActivity(agentId, (current) => {
        if (!current.deltaText) {
          return current
        }
        return {
          ...current,
          deltaText: '',
        }
      })
    },
    [updateAgentActivity],
  )

  const upsertToolActivity = useCallback(
    (agentId: string, entry: ToolActivityEntry) => {
      updateAgentActivity(agentId, (current) => {
        const deduped = current.badges.filter((badge) => badge.id !== entry.id)
        const nextBadges = [entry, ...deduped].slice(0, MAX_ACTIVITY_BADGES)
        return {
          ...current,
          badges: nextBadges,
        }
      })
    },
    [updateAgentActivity],
  )

  const attachToolResult = useCallback(
    (agentId: string, toolCallId: string, content: string) => {
      updateAgentActivity(agentId, (current) => {
        const nextBadges = current.badges.map((badge) =>
          badge.id === toolCallId ? { ...badge, resultContent: content } : badge,
        )
        return {
          ...current,
          badges: nextBadges,
        }
      })
    },
    [updateAgentActivity],
  )

  const handleToolCallEvent = useCallback(
    (agentId: string, payload: any) => {
      if (!agentId || !payload?.toolCall) {
        return
      }
      const toolCall = payload.toolCall
      const toolName = toolCall.function?.name
      const args = stringifyToolArguments(toolCall.function?.arguments)
      const id = typeof toolCall.id === 'string' ? toolCall.id : generateAssignmentId()
      pendingToolCallAgentRef.current.set(id, agentId)
      upsertToolActivity(agentId, {
        id,
        toolName,
        content: args,
        timestamp: Date.now(),
      })
    },
    [upsertToolActivity],
  )

  const handleToolResultEvent = useCallback(
    (agentId: string, payload: any) => {
      if (!payload?.result) {
        return
      }
      const result = payload.result
      const targetId = typeof result.tool_call_id === 'string' ? result.tool_call_id : null
      if (!targetId) {
        return
      }
      const owner = pendingToolCallAgentRef.current.get(targetId) ?? agentId
      const normalizedContent = stringifyToolArguments(result.content)
      attachToolResult(owner, targetId, normalizedContent)
      pendingToolCallAgentRef.current.delete(targetId)
    },
    [attachToolResult],
  )

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    selectedAgentIdRef.current = selectedAgentId
  }, [selectedAgentId])

  useEffect(() => {
    if (!agents.length) {
      if (selectedAgentId !== null) {
        setSelectedAgentId(null)
      }
      return
    }
    if (selectedAgentId && agents.some((agent) => agent.id === selectedAgentId)) {
      return
    }
    const fallback =
      agents.find((agent) => agent.id === activeAgentIdRef.current)?.id ?? agents[0].id
    setSelectedAgentId(fallback)
  }, [agents, selectedAgentId])

  useEffect(() => {
    activeAgentIdRef.current = activeAgentId
  }, [activeAgentId])

  useEffect(() => {
    setTaskDropIndex(null)
  }, [selectedAgentId])

  useEffect(() => {
    const unsubscribe = firehose.monitor((event, data: FirehoseEventPayload) => {
      if (!data || typeof data !== 'object') {
        return
      }
      const agentId = typeof data.agentId === 'string' ? data.agentId : null
      if (!agentId) {
        return
      }
      const payload = data.payload

      switch (event) {
        case 'llm/delta/text':
        case 'llm/delta/json':
          appendAgentDelta(agentId, normalizeDeltaPayload(payload))
          break
        case 'tool/call':
          handleToolCallEvent(agentId, payload)
          break
        case 'tool/result':
          handleToolResultEvent(agentId, payload)
          break
        case 'llm/run':
        case 'llm/start':
          setAgentStatusMap((prev) => ({
            ...prev,
            [agentId]: 'running',
          }))
          resetAgentDelta(agentId)
          break
        case 'llm/error':
          setAgentStatusMap((prev) => ({
            ...prev,
            [agentId]: 'error',
          }))
          break
        case 'llm/end':
        case 'llm/cancel':
        case 'llm/paused':
          setAgentStatusMap((prev) => ({
            ...prev,
            [agentId]: 'idle',
          }))
          break
        default:
          break
      }
    })

    return unsubscribe
  }, [appendAgentDelta, handleToolCallEvent, handleToolResultEvent, resetAgentDelta])

  const loadActivityFromHistory = useCallback(async (agentList: AgentRecord[]) => {
    if (!agentList.length) {
      return
    }
    const tasks = agentList.map(async (agent) => {
      if (!agent.last_message_id) {
        setAgentActivity((prev) => {
          if (prev[agent.id]) {
            return prev
          }
          return {
            ...prev,
            [agent.id]: prev[agent.id] ?? createEmptyActivityState(),
          }
        })
        return
      }
      try {
        const lastMessage = await api().services.context.getMessage(agent.last_message_id)
        if (!lastMessage || typeof lastMessage.session_id !== 'number') {
          return
        }
        const messages = (await api().services.context.getMessages(
          lastMessage.session_id,
          agent.last_message_id,
        )) as ContextMessage[]
        if (!mountedRef.current) {
          return
        }
        const activity = extractToolActivityFromMessages(messages)
        setAgentActivity((prev) => {
          const current = prev[agent.id] ?? createEmptyActivityState()
          return {
            ...prev,
            [agent.id]: {
              ...current,
              badges: activity,
            },
          }
        })
      } catch (err) {
        console.warn(`[AssignmentsPanel] Failed to load activity for agent ${agent.id}`, err)
      }
    })
    await Promise.allSettled(tasks)
  }, [])

  const hydrateAssignments = useCallback(() => {
    if (typeof window === 'undefined') {
      return
    }
    try {
      const stored = window.localStorage.getItem(ASSIGNMENT_STORAGE_KEY)
      if (!stored) {
        return
      }
      const parsed = JSON.parse(stored)
      if (!parsed || typeof parsed !== 'object') {
        return
      }
      const next: AssignmentMap = {}
      for (const [agentId, list] of Object.entries(parsed)) {
        if (!Array.isArray(list)) {
          continue
        }
        const normalizedList = list
          .map((entry: any) => {
            const content = typeof entry?.content === 'string' ? entry.content : ''
            if (!content) {
              return null
            }
            return {
              id: typeof entry?.id === 'string' ? entry.id : generateAssignmentId(),
              content,
              sourceHash: typeof entry?.sourceHash === 'string' ? entry.sourceHash : null,
              createdAt: typeof entry?.createdAt === 'number' ? entry.createdAt : Date.now(),
            }
          })
          .filter(Boolean) as AgentTaskAssignment[]
        if (normalizedList.length) {
          next[agentId] = normalizedList
        }
      }
      setAssignments(next)
    } catch (err) {
      console.warn('[AssignmentsPanel] Failed to parse stored assignments', err)
    }
  }, [])

  useEffect(() => {
    hydrateAssignments()
  }, [hydrateAssignments])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    try {
      window.localStorage.setItem(ASSIGNMENT_STORAGE_KEY, JSON.stringify(assignments))
    } catch (err) {
      console.warn('[AssignmentsPanel] Failed to persist assignments', err)
    }
  }, [assignments])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    const subscribeAgent = window.subscribeAgent
    const unsubscribeAgent = window.unsubscribeAgent
    if (typeof subscribeAgent !== 'function' || typeof unsubscribeAgent !== 'function') {
      return
    }
    const desired = new Set(
      Object.entries(assignments)
        .filter(([, list]) => Array.isArray(list) && list.length > 0)
        .map(([agentId]) => agentId)
        .filter((agentId) => agentId && agentId !== activeAgentId),
    )
    const subscribed = subscribedAgentsRef.current

    for (const agentId of Array.from(subscribed)) {
      if (!desired.has(agentId)) {
        unsubscribeAgent(agentId)
        subscribed.delete(agentId)
      }
    }

    for (const agentId of desired) {
      if (!subscribed.has(agentId)) {
        if (subscribeAgent(agentId)) {
          subscribed.add(agentId)
        }
      }
    }
  }, [assignments, activeAgentId])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    const unsubscribeAgent = window.unsubscribeAgent
    if (typeof unsubscribeAgent !== 'function') {
      return
    }
    return () => {
      for (const agentId of subscribedAgentsRef.current) {
        unsubscribeAgent(agentId)
      }
      subscribedAgentsRef.current.clear()
    }
  }, [])

  const loadActiveAgentId = useCallback(async () => {
    try {
      const stored = await api().services.meta.get('active_agent_id')
      if (!mountedRef.current) {
        return
      }
      if (typeof stored === 'string') {
        setActiveAgentId(stored)
        setSelectedAgentId((prev) => prev ?? stored)
      }
    } catch (err) {
      console.warn('[AssignmentsPanel] Failed to load active agent id', err)
    }
  }, [])

  useEffect(() => {
    void loadActiveAgentId()
  }, [loadActiveAgentId])

  const loadAgents = useCallback(async () => {
    setPanelError(null)
    setLoadingAgents(true)
    try {
      const list = await api().services.agent.list()
      if (!mountedRef.current) {
        return
      }
      const mapped = (list ?? []).map(normalizeAgent)
      setAgents(mapped)
      setAgentStatusMap((prev) => {
        const next = { ...prev }
        mapped.forEach((agent) => {
          if (!next[agent.id] && (agent.state === 'running' || agent.state === 'error' || agent.state === 'idle')) {
            next[agent.id] = agent.state as 'running' | 'idle' | 'error'
          }
        })
        return next
      })
      if (!selectedAgentIdRef.current && mapped.length) {
        const fallback =
          mapped.find((agent) => agent.id === activeAgentIdRef.current)?.id ?? mapped[0].id
        setSelectedAgentId(fallback)
      }
      void loadActivityFromHistory(mapped)
    } catch (err) {
      console.error('[AssignmentsPanel] Failed to load agents:', err)
      if (mountedRef.current) {
        setPanelError('Failed to load agents')
      }
    } finally {
      if (mountedRef.current) {
        setLoadingAgents(false)
      }
    }
  }, [])

  useEffect(() => {
    void loadAgents()
  }, [loadAgents])

  const ensureAgentActive = useCallback(
    async (agentId: string) => {
      if (!agentId) {
        return false
      }
      if (activeAgentIdRef.current === agentId) {
        return true
      }
      try {
        await api().services.meta.set('active_agent_id', agentId)
        if (mountedRef.current) {
          setActiveAgentId(agentId)
        }
        return true
      } catch (err) {
        console.error('[AssignmentsPanel] Failed to activate agent:', err)
        if (mountedRef.current) {
          setPanelError('Failed to activate agent')
        }
        return false
      }
    },
    [],
  )

  const refreshStatus = useCallback(async () => {
    setStatusRefreshing(true)
    try {
      const status = await api().agent.status()
      if (!mountedRef.current) {
        return
      }
      const formatted = typeof status === 'string' ? status : JSON.stringify(status, null, 2)
      setStatusData(formatted)
    } catch (err) {
      console.error('[AssignmentsPanel] Failed to refresh agent status:', err)
      if (mountedRef.current) {
        setStatusData(JSON.stringify({ error: 'Unable to load agent status' }, null, 2))
      }
    } finally {
      if (mountedRef.current) {
        setStatusRefreshing(false)
      }
    }
  }, [])

  const refreshAgentById = useCallback(
    async (agentId: string) => {
      if (!agentId) {
        return
      }
      try {
        const record = await api().services.agent.get(agentId)
        if (!record || !mountedRef.current) {
          return
        }
        const normalized = normalizeAgent(record)
        setAgents((prev) => {
          const index = prev.findIndex((agent) => agent.id === normalized.id)
          if (index === -1) {
            return [...prev, normalized]
          }
          const next = [...prev]
          next[index] = normalized
          return next
        })
        setAgentStatusMap((prev) => {
          if (normalized.state !== 'running' && normalized.state !== 'idle' && normalized.state !== 'error') {
            return prev
          }
          if (prev[normalized.id] === normalized.state) {
            return prev
          }
          return {
            ...prev,
            [normalized.id]: normalized.state,
          }
        })
        void loadActivityFromHistory([normalized])
        if (selectedAgentIdRef.current === normalized.id && activeAgentIdRef.current === normalized.id) {
          void refreshStatus()
        }
      } catch (err) {
        console.error(`[AssignmentsPanel] Failed to refresh agent ${agentId}`, err)
      }
    },
    [loadActivityFromHistory, refreshStatus],
  )

  const applyAgentPatch = useCallback(
    (payload: any) => {
      const agentId = typeof payload?.id === 'string' ? payload.id : null
      if (!agentId) {
        return
      }
      let updatedAgent: AgentRecord | null = null
      setAgents((prev) => {
        const index = prev.findIndex((agent) => agent.id === agentId)
        if (index === -1) {
          return prev
        }
        const merged = normalizeAgent({ ...prev[index], ...payload })
        updatedAgent = merged
        const next = [...prev]
        next[index] = merged
        return next
      })
      if (!updatedAgent) {
        void refreshAgentById(agentId)
        return
      }
      setAgentStatusMap((prev) => {
        if (updatedAgent?.state !== 'running' && updatedAgent?.state !== 'idle' && updatedAgent?.state !== 'error') {
          return prev
        }
        if (prev[agentId] === updatedAgent.state) {
          return prev
        }
        return {
          ...prev,
          [agentId]: updatedAgent.state,
        }
      })
      void loadActivityFromHistory([updatedAgent])
      if (selectedAgentIdRef.current === agentId && activeAgentIdRef.current === agentId) {
        void refreshStatus()
      }
    },
    [loadActivityFromHistory, refreshAgentById, refreshStatus],
  )

  useEffect(() => {
    const unsubscribeCreate = bus.on('agent/create', (_event, payload: any) => {
      const id = typeof payload?.id === 'string' ? payload.id : null
      if (id) {
        void refreshAgentById(id)
        return
      }
      void loadAgents()
    })
    const unsubscribeUpdate = bus.on('agent/update', (_event, payload: any) => {
      applyAgentPatch(payload)
    })
    const unsubscribeDelete = bus.on('agent/delete', (_event, payload: any) => {
      const id = typeof payload?.id === 'string' ? payload.id : null
      if (!id) {
        return
      }
      setAgents((prev) => prev.filter((agent) => agent.id !== id))
      setAssignments((prev) => {
        if (!prev[id]) {
          return prev
        }
        const { [id]: _removed, ...rest } = prev
        return rest
      })
      setAgentActivity((prev) => {
        if (!prev[id]) {
          return prev
        }
        const { [id]: _removed, ...rest } = prev
        return rest
      })
      setAgentStatusMap((prev) => {
        if (!(id in prev)) {
          return prev
        }
        const { [id]: _removed, ...rest } = prev
        return rest
      })
    })
    const unsubscribeMeta = bus.on('meta/set', (_event, payload: any) => {
      if (payload?.key === 'active_agent_id' && typeof payload?.value === 'string') {
        setActiveAgentId(payload.value)
      }
    })
    return () => {
      unsubscribeCreate()
      unsubscribeUpdate()
      unsubscribeDelete()
      unsubscribeMeta()
    }
  }, [applyAgentPatch, loadAgents, refreshAgentById])

  const handleSelectAgent = useCallback(
    async (agentId: string) => {
      if (!agentId) {
        return
      }
      setPanelError(null)
      setSelectedAgentId(agentId)
      await ensureAgentActive(agentId)
    },
    [ensureAgentActive],
  )

  const addAssignment = useCallback((agentId: string, content: string, hash: string | null, insertIndex?: number) => {
    setAssignments((prev) => {
      const current = prev[agentId] ?? []
      const nextTask: AgentTaskAssignment = {
        id: generateAssignmentId(),
        content,
        sourceHash: hash,
        createdAt: Date.now(),
      }
      const nextList = [...current]
      if (typeof insertIndex === 'number' && insertIndex >= 0 && insertIndex <= nextList.length) {
        nextList.splice(insertIndex, 0, nextTask)
      } else {
        nextList.push(nextTask)
      }
      return {
        ...prev,
        [agentId]: nextList,
      }
    })
  }, [])

  const ingestDroppedTask = useCallback(
    (agentId: string, payload: string | null, insertIndex?: number) => {
      if (!agentId) {
        return false
      }
      if (!payload) {
        setPanelError('No task data found in drop payload')
        return false
      }
      try {
        const parsed = JSON.parse(payload)
        const content = typeof parsed?.content === 'string' ? parsed.content : ''
        if (!content) {
          setPanelError('Dropped task had no content')
          return false
        }
        addAssignment(agentId, content, typeof parsed?.hash === 'string' ? parsed.hash : null, insertIndex)
        return true
      } catch {
        const fallback = payload.trim()
        if (!fallback) {
          setPanelError('Dropped task had no content')
          return false
        }
        addAssignment(agentId, fallback, null, insertIndex)
        return true
      }
    },
    [addAssignment],
  )

  const handleDropOnAgent = (agentId: string, event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setDropTargetAgentId(null)
    setPanelError(null)
    const payload =
      event.dataTransfer.getData(TASK_DRAG_TYPE) || event.dataTransfer.getData('text/plain')
    void ingestDroppedTask(agentId, payload)
  }

  const handleTaskRemove = (agentId: string, taskId: string) => {
    setAssignments((prev) => {
      const current = prev[agentId]
      if (!current) {
        return prev
      }
      const nextList = current.filter((task) => task.id !== taskId)
      if (nextList.length === current.length) {
        return prev
      }
      if (!nextList.length) {
        const { [agentId]: _removed, ...rest } = prev
        return rest
      }
      return {
        ...prev,
        [agentId]: nextList,
      }
    })
  }

  const allowTaskDrop = (event: React.DragEvent<HTMLElement>) => {
    const types = Array.from(event.dataTransfer?.types ?? [])
    const allowed =
      types.includes(ASSIGNMENT_DRAG_TYPE) ||
      types.includes(TASK_DRAG_TYPE) ||
      types.includes('text/plain')
    if (allowed) {
      event.preventDefault()
      event.dataTransfer.dropEffect = types.includes(ASSIGNMENT_DRAG_TYPE) ? 'move' : 'copy'
    }
    return allowed
  }

  const findTaskInsertionIndex = (event: React.DragEvent<HTMLElement>) => {
    const container = taskListRef.current
    if (!container) {
      return 0
    }
    const tasks = Array.from(container.querySelectorAll<HTMLElement>('.agent-task'))
    const { clientY } = event
    for (let index = 0; index < tasks.length; index += 1) {
      const rect = tasks[index].getBoundingClientRect()
      const midpoint = rect.top + rect.height / 2
      if (clientY < midpoint) {
        return index
      }
    }
    return tasks.length
  }

  const reorderAssignmentById = useCallback((agentId: string, taskId: string, targetIndex: number) => {
    setAssignments((prev) => {
      const current = prev[agentId]
      if (!current) {
        return prev
      }
      const fromIndex = current.findIndex((task) => task.id === taskId)
      if (fromIndex === -1 || targetIndex === fromIndex || targetIndex === fromIndex + 1) {
        return prev
      }
      const next = [...current]
      const [moved] = next.splice(fromIndex, 1)
      if (!moved) {
        return prev
      }
      const rawInsertIndex = fromIndex < targetIndex ? targetIndex - 1 : targetIndex
      const insertIndex = Math.max(0, Math.min(next.length, rawInsertIndex))
      next.splice(insertIndex, 0, moved)
      return {
        ...prev,
        [agentId]: next,
      }
    })
  }, [])

  const handleTaskDragStart = (agentId: string, taskId: string) => (event: React.DragEvent<HTMLLIElement>) => {
    draggingTaskRef.current = { agentId, taskId }
    taskDropHandledRef.current = false
    try {
      event.dataTransfer.setData(ASSIGNMENT_DRAG_TYPE, JSON.stringify({ agentId, taskId }))
    } catch {
      // Ignore serialization issues; reorder still works via refs
    }
    event.dataTransfer.setData('text/plain', taskId)
    event.dataTransfer.dropEffect = 'move'
    event.dataTransfer.effectAllowed = 'move'
  }

  const handleTaskDragEnd = () => {
    if (!taskDropHandledRef.current) {
      const dragged = draggingTaskRef.current
      if (dragged) {
        handleTaskRemove(dragged.agentId, dragged.taskId)
      }
    }
    setTaskDropIndex(null)
    draggingTaskRef.current = null
    taskDropHandledRef.current = false
  }

  const handleTaskListDragOver = (event: React.DragEvent<HTMLElement>) => {
    if (!allowTaskDrop(event)) {
      return
    }
    const index = findTaskInsertionIndex(event)
    setTaskDropIndex(index)
  }

  const handleTaskListDragLeave = (event: React.DragEvent<HTMLElement>) => {
    const related = event.relatedTarget as Node | null
    if (related && event.currentTarget.contains(related)) {
      return
    }
    setTaskDropIndex(null)
  }

  const handleTaskListDrop = (agentId: string, event: React.DragEvent<HTMLElement>) => {
    if (!agentId) {
      return
    }
    if (!allowTaskDrop(event)) {
      return
    }
    event.preventDefault()
    event.stopPropagation()
    setPanelError(null)
    const targetIndex = findTaskInsertionIndex(event)
    setTaskDropIndex(null)
    const assignmentData = event.dataTransfer.getData(ASSIGNMENT_DRAG_TYPE)
    if (assignmentData) {
      try {
        const parsed = JSON.parse(assignmentData)
        if (parsed?.agentId === agentId && typeof parsed?.taskId === 'string') {
          reorderAssignmentById(agentId, parsed.taskId, targetIndex)
          taskDropHandledRef.current = true
          return
        }
      } catch {
        // ignore malformed payload
      }
    }
    const payload = event.dataTransfer.getData(TASK_DRAG_TYPE) || event.dataTransfer.getData('text/plain')
    if (payload) {
      if (ingestDroppedTask(agentId, payload, targetIndex)) {
        taskDropHandledRef.current = true
      }
      return
    }
    setPanelError('No task data found in drop payload')
  }

  const selectedAgent = useMemo(() => {
    if (!selectedAgentId) {
      return null
    }
    return agents.find((agent) => agent.id === selectedAgentId) ?? null
  }, [agents, selectedAgentId])

  const selectedAssignments = assignments[selectedAgentId ?? ''] ?? []
  const canRefreshStatus = Boolean(selectedAgentId && selectedAgentId === activeAgentId)
  const selectedActivity = selectedAgentId ? agentActivity[selectedAgentId] : undefined
  const selectedToolBadges = selectedActivity?.badges ?? []
  const selectedDeltaText = selectedActivity?.deltaText ?? ''

  useEffect(() => {
    if (!selectedAgentId || !activeAgentId) {
      return
    }
    if (selectedAgentId !== activeAgentId) {
      return
    }
    if (lastStatusAgentRef.current === selectedAgentId) {
      return
    }
    lastStatusAgentRef.current = selectedAgentId
    void refreshStatus()
  }, [selectedAgentId, activeAgentId, refreshStatus])

  const agentListContent = () => {
    if (!agents.length) {
      return (
        <div className="assignments-panel__placeholder">
          <p>{loadingAgents ? 'Loading agents…' : 'No agents available'}</p>
          {!loadingAgents && <p className="empty-state-subtitle">Create an agent before assigning tasks</p>}
        </div>
      )
    }
    return (
      <ul className="agent-list">
        {agents.map((agent) => {
          const isSelected = agent.id === selectedAgentId
          const isDropping = dropTargetAgentId === agent.id
          const activityState = agentActivity[agent.id]
          const liveDelta = activityState?.deltaText ?? ''
          const modelLabel = agent.model ? modelLabelMap[agent.model] ?? agent.model : 'Model not set'
          const classes = [
            'agent-list__row',
            isSelected ? 'agent-list__row--selected' : '',
            isDropping ? 'agent-list__row--dropping' : '',
          ]
            .filter(Boolean)
            .join(' ')
          return (
            <li key={agent.id}>
              <button
                type="button"
                className={classes}
                onClick={() => void handleSelectAgent(agent.id)}
                onDragOver={(event) => {
                  event.preventDefault()
                  event.dataTransfer.dropEffect = 'copy'
                  setDropTargetAgentId(agent.id)
                }}
                onDragLeave={(event) => {
                  event.preventDefault()
                  if (dropTargetAgentId === agent.id) {
                    setDropTargetAgentId(null)
                  }
                }}
                onDrop={(event) => handleDropOnAgent(agent.id, event)}
              >
                <div className="agent-list__row-header">
                  <div className="agent-list__name-section">
                    <div className="agent-list__name-row">
                      <span className="agent-list__name">{agent.name || 'Untitled agent'}</span>
                    </div>
                    <span className="agent-list__model-label">{modelLabel}</span>
                  </div>
                </div>
                <AgentDeltaTicker
                  className="agent-delta-ticker"
                  placeholder="No live output"
                  text={liveDelta}
                />
              </button>
            </li>
          )
        })}
      </ul>
    )
  }

  const assignmentsContent = () => {
    if (!selectedAgent) {
      return (
        <div className="assignments-panel__placeholder">
          <p>Select an agent to view assignments</p>
        </div>
      )
    }
    const detailStatus: 'running' | 'idle' | 'error' =
      agentStatusMap[selectedAgent.id] ??
      (selectedAgent.state === 'error'
        ? 'error'
        : selectedAgent.state === 'running'
        ? 'running'
        : 'idle')
    return (
      <>
        <section className="agent-activity">
          <header className="agent-activity__header">
            <p className="agent-activity__title">Recent activity</p>
          </header>
          <div className={`agent-activity__badges${selectedToolBadges.length === 0 ? ' agent-activity__badges--empty' : ''}`}>
            {selectedToolBadges.length === 0 ? (
              <span className="agent-activity__empty">No tool calls yet</span>
            ) : (
              selectedToolBadges.map((tool) => (
                <ToolCallBadge
                  key={tool.id}
                  toolName={tool.toolName}
                  content={tool.content}
                  resultContent={tool.resultContent}
                />
              ))
            )}
          </div>
          <AgentDeltaTicker
            className="agent-activity__delta-line"
            placeholder="Live deltas will appear here"
            text={selectedDeltaText}
          />
        </section>
        <section
          className={`agent-tasks${taskDropIndex !== null ? ' agent-tasks--dropping' : ''}`}
          onDragOver={handleTaskListDragOver}
          onDragLeave={handleTaskListDragLeave}
          onDrop={(event) => handleTaskListDrop(selectedAgent.id, event)}
        >
          <header className="agent-tasks__header">
            <p className="agent-tasks__title">Assigned tasks</p>
            <div className="agent-tasks__count">
              {selectedAssignments.length} {selectedAssignments.length === 1 ? 'task' : 'tasks'}
            </div>
          </header>
          {selectedAssignments.length === 0 ? (
            <div className={`assignments-panel__placeholder${taskDropIndex !== null ? ' assignments-panel__placeholder--dropping' : ''}`}>
              <p>No tasks queued</p>
              <p className="empty-state-subtitle">Drag a task to this agent to get started</p>
            </div>
          ) : (
            <ul
              className={`agent-task-list${
                taskDropIndex !== null && taskDropIndex === selectedAssignments.length
                  ? ' agent-task-list--drop-after'
                  : ''
              }`}
              ref={taskListRef}
            >
              {selectedAssignments.map((task, index) => (
                <li
                  key={task.id}
                  className={`agent-task task-card${
                    taskDropIndex === index ? ' agent-task--drop-before' : ''
                  }${taskDropIndex === index + 1 ? ' agent-task--drop-after' : ''}`}
                  draggable
                  onDragStart={handleTaskDragStart(selectedAgent.id, task.id)}
                  onDragEnd={handleTaskDragEnd}
                >
                  <span className="task-card__title">{task.content}</span>
                  <div className="task-card__meta">
                    <span className="task-card__meta-text">{formatAssignmentTimestamp(task.createdAt)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section className="agent-status-viewer">
          <header className="agent-status-viewer__header">
            <p className="agent-status-viewer__title">Agent status</p>
            <button
              type="button"
              className="btn"
              onClick={() => void refreshStatus()}
              disabled={statusRefreshing || !canRefreshStatus}
            >
              {statusRefreshing ? 'Loading…' : canRefreshStatus ? 'Refresh status' : 'Activate agent'}
            </button>
            <span className={`agent-status-badge agent-status-badge--${detailStatus}`}>
              {detailStatus}
            </span>
          </header>
          <TextContentViewer content={statusData} readOnly={true} language="json" />
        </section>
      </>
    )
  }

  return (
    <div className="panel-shell assignments-panel">
      <div className="panel-controls">
        {panelError && <span className="panel-error">{panelError}</span>}
      </div>
      <div className="panel-content assignments-panel__content">
        <div className="assignments-panel__column assignments-panel__column--list">{agentListContent()}</div>
        <div className="assignments-panel__column assignments-panel__column--detail">{assignmentsContent()}</div>
      </div>
    </div>
  )
}

function AgentDeltaTicker({ text, className, placeholder }: { text: string; className?: string; placeholder: string }) {
  const lineRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const element = lineRef.current
    if (!element) {
      return
    }
    element.scrollLeft = element.scrollWidth
  }, [text])

  const content = text?.trim() ?? ''
  const classes = [className ?? '', 'agent-delta-line', content ? '' : 'agent-delta-line--empty']
    .filter(Boolean)
    .join(' ')

  return (
    <div ref={lineRef} className={classes} aria-live="polite">
      {content || placeholder}
    </div>
  )
}
